import type { CVData } from "../data/cvTemplate";
import { setTemplatePaidStatuses } from "../data/planRules";
import { apiRequest } from "./apiClient";

export type CVTemplate = {
  key: string;
  label: string;
  description: string;
  accentColor: string;
  family?: string;
  data?: CVData; 
  isActive?: boolean;
  isPaid?: boolean;
};

/** Fallback list used when the backend is unreachable */
export const visualTemplates: CVTemplate[] = [
  { key: "classic-old", label: "Legacy", description: "The original ResumeX legacy layout.", accentColor: "#4f46e5", isPaid: false }, 
  { key: "classic", label: "Classic", description: "A traditional, single-column layout.", accentColor: "#4f46e5", isPaid: true },
  { key: "modern", label: "Modern", description: "A sleek 2-column layout.", accentColor: "#2563eb", isPaid: true },
  { key: "executive", label: "Executive", description: "Formal, top-aligned corporate layout.", accentColor: "#334155", isPaid: true },
  { key: "minimal", label: "Minimal", description: "Maximum whitespace and clean lines.", accentColor: "#0f766e", isPaid: true },
  { key: "creative", label: "Creative", description: "Bold headers and dynamic tags.", accentColor: "#9333ea", isPaid: true },
  { key: "technical", label: "Technical", description: "Dense layout prioritizing skills.", accentColor: "#059669", isPaid: true },
  { key: "sharp", label: "Sharp", description: "Crisp, angled design elements.", accentColor: "#27272a", isPaid: true },
  { key: "slate", label: "Slate", description: "Dark, modern monochromatic accents.", accentColor: "#1d4ed8", isPaid: true },
  { key: "horizon", label: "Horizon", description: "Wide landscape-style headers.", accentColor: "#d97706", isPaid: true },
  { key: "compact", label: "Compact", description: "High-density layout for long CVs.", accentColor: "#be123c", isPaid: true },
  { key: "spark", label: "Spark", description: "Bold solid-color sidebar with white text sections.", accentColor: "#7c3aed", isPaid: true },
  { key: "vivid", label: "Vivid", description: "Gradient hero header with tinted section bands.", accentColor: "#e11d48", isPaid: true },
  { key: "mono", label: "Mono", description: "Pure Swiss typography — no decorative elements.", accentColor: "#525252", isPaid: true },
  { key: "pillar", label: "Pillar", description: "Academic date-column grid layout.", accentColor: "#1d4ed8", isPaid: true },
  { key: "crisp", label: "Crisp", description: "Nordic Scandinavian minimalism.", accentColor: "#0891b2", isPaid: true },
];

/**
 * Fetch active templates from the backend.
 * Falls back to the hardcoded list ONLY when the request throws (network error,
 * server unreachable, etc.). An intentionally empty list from the backend
 * (all templates deactivated) is respected as-is.
 */
export const listTemplates = async (): Promise<CVTemplate[]> => {
  try {
    const result = await apiRequest<CVTemplate[]>("/cv-templates");
    if (Array.isArray(result)) {
      setTemplatePaidStatuses(result);
      return result;
    }
  } catch {
    // silent fallback — backend unreachable
  }
  setTemplatePaidStatuses(visualTemplates);
  return visualTemplates;
};

export const getTemplate = async (key: string): Promise<CVTemplate> => {
  const template = visualTemplates.find((t) => t.key === key);
  if (!template) throw new Error("Template not found");
  return template;
};